import type { CSSProperties } from "react";
import { useReducedMotion, type Transition, type Variants } from "motion/react";

export const motionDurations = {
  instant: 80,
  fast: 120,
  normal: 160,
  panel: 180,
  content: 200,
  container: 240
} as const;

export const motionEasings = {
  standard: "cubic-bezier(.2, 0, 0, 1)",
  emphasized: "cubic-bezier(.2, .8, .2, 1)"
} as const;

export const motionEasingCurves = {
  standard: [0.2, 0, 0, 1],
  emphasized: [0.2, 0.8, 0.2, 1]
} as const;

export const motionVariants = {
  fade: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  },
  routeFade: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  },
  fadeThrough: {
    initial: { opacity: 0, scale: 0.995 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.995 }
  },
  popover: {
    initial: { opacity: 0, y: -4, scale: 0.98 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: -4, scale: 0.98 }
  },
  modal: {
    initial: { opacity: 0, y: 12, scale: 0.98 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: 8, scale: 0.98 }
  },
  drawer: {
    initial: { opacity: 0, x: "-104%" },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: "-104%" }
  },
  bottomBar: {
    initial: { opacity: 0, y: 8 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 6 }
  },
  containerOpen: {
    initial: { opacity: 0, y: 8, scale: 0.985 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: 4, scale: 0.99 }
  }
} satisfies Record<string, Variants>;

export type MotionSurface = keyof typeof motionVariants;

const motionTransitions = {
  fade: {
    duration: motionDurations.normal / 1000,
    ease: motionEasingCurves.standard
  },
  routeFade: {
    duration: motionDurations.fast / 1000,
    ease: motionEasingCurves.standard
  },
  fadeThrough: {
    duration: motionDurations.content / 1000,
    ease: motionEasingCurves.standard
  },
  popover: {
    duration: motionDurations.fast / 1000,
    ease: motionEasingCurves.standard
  },
  modal: {
    duration: motionDurations.panel / 1000,
    ease: motionEasingCurves.emphasized
  },
  drawer: {
    duration: motionDurations.panel / 1000,
    ease: motionEasingCurves.standard
  },
  bottomBar: {
    duration: motionDurations.normal / 1000,
    ease: motionEasingCurves.standard
  },
  containerOpen: {
    duration: motionDurations.container / 1000,
    ease: motionEasingCurves.emphasized
  }
} satisfies Record<MotionSurface, Transition>;

export function useReducedMotionSafe() {
  return useReducedMotion() === true;
}

export function motionTransition(
  surface: MotionSurface,
  reduceMotion: boolean
): Transition {
  return reduceMotion
    ? { duration: motionDurations.instant / 1000, ease: "linear" }
    : motionTransitions[surface];
}

export function workCoverViewTransitionName(workId: string) {
  return `work-cover-${workId.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
}

export function workCoverViewTransitionStyle(workId: string): CSSProperties {
  return {
    viewTransitionName: workCoverViewTransitionName(workId)
  };
}
